import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { QueryResponse } from '../../core/services/QueryResponse';
import { ModelResponse } from '../../core/services/ModelResponse';
import { DynamicDataSetListModel } from '../models/DynamicDataSetListModel';
import { DynamicDataSetModel } from '../models/DynamicDataSetModel';
import { DynamicEntityModel } from '../models/DynamicEntityModel';
import { PropertyModel } from '../models/PropertyModel';
import { DataLinkModel } from '../models/DataLinkModel';
import { DynamicRuleProfileModel } from '../models/DynamicRuleProfileModel';
import { RuleExpressionModel } from '../models/RuleExpressionModel';
import { LogicalExpressionModel } from '../models/LogicalExpressionModel';
import { DynamicRuleModel } from '../models/DynamicRuleModel';
import { AddRuleConditionGroupModel } from '../models/AddRuleConditionGroupModel';

@Injectable()
export class DynamicDataSetService {
  constructor(public httpClient: HttpClient) {}

  getDynamicDataSetList(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string): Observable<QueryResponse<DynamicDataSetListModel>> {
    var params = new HttpParams()
      .set('skip', (page * pageSize).toString())
      .set('take', pageSize.toString());
    if (sort) {
      params = params.set('sort', sort)
        .set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<DynamicDataSetListModel>>("/api/DynamicDataSetModule/DynamicDataSet/ListQuery", { params: params });
  }

  getDynamicDataSet(key: string): Observable<ModelResponse<DynamicDataSetModel>> {
    return this.httpClient.get<ModelResponse<DynamicDataSetModel>>("/api/DynamicDataSetModule/DynamicDataSet/Get/" + key);
  }

  deleteDynamicDataSet(key: string): Observable<ModelResponse<string>> {
    return this.httpClient.delete<ModelResponse<string>>("/api/DynamicDataSetModule/DynamicDataSet/" + key);
  }

  getDynamicRule(key: string, ruleKey: string): Observable<ModelResponse<DynamicRuleModel>> {
    return this.httpClient.get<ModelResponse<DynamicRuleModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey);
  }

  finalizeDynamicDataSet(key: string): Observable<ModelResponse<DynamicDataSetModel>> {
    return this.httpClient.get<ModelResponse<DynamicDataSetModel>>("/api/DynamicDataSetModule/DynamicDataSet/Finalize/" + key);
  }

  unfinalizeDynamicDataSet(key: string): Observable<ModelResponse<DynamicDataSetModel>> {
    return this.httpClient.get<ModelResponse<DynamicDataSetModel>>("/api/DynamicDataSetModule/DynamicDataSet/Unfinalize/" + key);
  }

  createDraftDynamicDataSet(key: string): Observable<ModelResponse<DynamicDataSetModel>> {
    return this.httpClient.get<ModelResponse<DynamicDataSetModel>>("/api/DynamicDataSetModule/DynamicDataSet/CreateDraft/" + key);
  }

  addDynamicDataSet(addModel: string): Observable<ModelResponse<string>> {
    return this.httpClient.put<ModelResponse<string>>("/api/DynamicDataSetModule/DynamicDataSet/Add/", addModel);
  }

  reviseEntity(key: string, model: DynamicEntityModel): Observable<ModelResponse<DynamicEntityModel>> {
    return this.httpClient.post<ModelResponse<DynamicEntityModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Revise", model);
  }

  addProperty(key: string, model: PropertyModel): Observable<ModelResponse<PropertyModel>> {
    return this.httpClient.put<ModelResponse<PropertyModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Property/Add", model);
  }

  addDataLink(key: string, propertyKey: string, model: DataLinkModel): Observable<ModelResponse<DataLinkModel>> {
    return this.httpClient.put<ModelResponse<DataLinkModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Property/" + propertyKey + "/AddDataLink", model);
  }

  addRule(key: string, model: DynamicRuleProfileModel): Observable<ModelResponse<DynamicRuleProfileModel>> {
    return this.httpClient.put<ModelResponse<DynamicRuleProfileModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/Add", model);
  }

  reviseDataLink(key: string, propertyKey: string, model: DataLinkModel): Observable<ModelResponse<DataLinkModel>> {
    return this.httpClient.post<ModelResponse<DataLinkModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Property/" + propertyKey + "/DataLink/Revise", model);
  }

  reviseProperty(key: string, propertyKey: string, model: PropertyModel): Observable<ModelResponse<PropertyModel>> {
    return this.httpClient.post<ModelResponse<PropertyModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Property/" + propertyKey + "/Revise", model);
  }

  reviseRule(key: string, ruleKey: string, model: DynamicRuleProfileModel): Observable<ModelResponse<DynamicRuleProfileModel>> {
    return this.httpClient.post<ModelResponse<DynamicRuleProfileModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/Revise", model);
  }

  deleteDataLink(key: string, propertyKey: string, model: DataLinkModel): Observable<ModelResponse<DataLinkModel>> {
    return this.httpClient.delete<ModelResponse<DataLinkModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Property/" + propertyKey + "/DataLink/" + model.key);
  }

  deleteProperty(key: string, model: PropertyModel): Observable<ModelResponse<PropertyModel>> {
    return this.httpClient.delete<ModelResponse<PropertyModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Property/" + model.key);
  }

  deleteRule(key: string, model: DynamicRuleProfileModel): Observable<ModelResponse<DynamicRuleProfileModel>> {
    return this.httpClient.delete<ModelResponse<DynamicRuleProfileModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + model.key);
  }

  reviseRuleExpression(key: string, ruleKey: string, model: RuleExpressionModel): Observable<ModelResponse<RuleExpressionModel>> {
    return this.httpClient.post<ModelResponse<RuleExpressionModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/Expression/Revise", model);
  }

  addRuleCondition(key: string, ruleKey: string, model: LogicalExpressionModel): Observable<ModelResponse<LogicalExpressionModel>> {
    return this.httpClient.put<ModelResponse<LogicalExpressionModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/AddCondition", model);
  }

  reviseRuleCondition(key: string, ruleKey: string, model: LogicalExpressionModel): Observable<ModelResponse<LogicalExpressionModel>> {
    return this.httpClient.post<ModelResponse<LogicalExpressionModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/Condition/Revise", model);
  }

  deleteRuleCondition(key: string, ruleKey: string, model: LogicalExpressionModel): Observable<ModelResponse<LogicalExpressionModel>> {
    return this.httpClient.delete<ModelResponse<LogicalExpressionModel>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/Condition/" + model.hashCode);
  }

  groupRuleCondition(key: string, ruleKey: string, model: AddRuleConditionGroupModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/Condition/Group", model);
  }

  ungroupRuleCondition(key: string, ruleKey: string, groupName: string): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>("/api/DynamicDataSetModule/DynamicDataSet/" + key + "/Entity/Rule/" + ruleKey + "/Condition/Ungroup", { model: groupName });
  }
}



// WEBPACK FOOTER //
// ./src/app/dynamicDataSets/service/DynamicDataSetService.ts